// Company Info Page for US Incorporation
import { useJurisdictions } from '@commenda/api';
import { ArrowNarrowRight, InfoToolTip } from '@commenda/icons';
import {
  AddressInput,
  Button,
  FieldWrapper,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Tooltip,
} from '@commenda/kakashi';
import { defaultAddressSchema } from '@commenda/schema/address';
import { IAddress, IncorporationRequestDataType } from '@commenda/types';
import { YesNoNotSureOptions } from '@commenda/types/src/common/enums';
import {
  BusinessPurposeEnum,
  TSetNotification,
} from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { addressesEqual, fetcher } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AddressType,
  CorporationType,
  Country,
  IncorporationRequestStatus,
} from '@prisma/client';
import { Country as CountryPackage } from 'country-state-city';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { KeyedMutator } from 'swr';
import RadioCardKS from '../../../components/RadioCardKS';
import FiscalMonthSelectMenuKS from '../common/FiscalMonthSelectMenuKS';
import { IncorporationFormHeading } from '../common/IncorporationFormElements';
import { jurisdictionToSuffixMap } from './common';
import { CompanyInfoFormFields, companyInfoSchema } from './schema';

const CompanyInfo = ({
  next,
  data,
  mutate,
  setNotification,
  token,
}: {
  next: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  token: string;
  setNotification: TSetNotification;
}) => {
  const suggestedMonthNumber = '12';
  const router = useRouter();

  const { id } = router.query;

  const { optionsFor } = useJurisdictions();
  // "Other" is the wizard asking rather than an answer, so it is local state. A state we do not
  // feature keeps the picker open on its own, which is how a resumed request restores it.
  const [isChoosingOtherState, setIsChoosingOtherState] = useState(false);

  const mailingAddress = data?.addresses?.find(
    (address) => address.type === AddressType.MailingAddress
  )?.value;

  const operatingAddress = data?.addresses?.find(
    (address) => address.type === AddressType.OperatingAddress
  )?.value;

  const shouldHideCorporationType =
    data?.task?.id && data?.requestData?.corporationType;

  const initialMailingOption: 'service' | 'manual' | 'sameAsPrincipal' =
    (() => {
      if (data?.requestData?.optForRegisterredAddressService) return 'service';
      const hasOp = !!operatingAddress;
      const hasMail = !!mailingAddress;
      if (
        hasOp &&
        hasMail &&
        addressesEqual(
          operatingAddress as IAddress,
          { ...mailingAddress, country: Country.US } as IAddress
        )
      ) {
        return 'sameAsPrincipal';
      }
      return 'manual';
    })();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    clearErrors,
    unregister,
  } = useForm<CompanyInfoFormFields>({
    resolver: zodResolver(companyInfoSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      jurisdiction: data?.requestData?.incorporationJurisdiction,
      corporationType: data?.requestData?.corporationType,
      corporationName: data?.requestData?.companyLegalName,
      companyLegalName2: data?.requestData?.companyLegalName2,
      companySuffix: data?.requestData?.companyNameSuffix || 'Inc.',
      optForRegisterredAddressService:
        data?.requestData?.optForRegisterredAddressService ?? false,
      isMemberManagedOrManagerManaged:
        data?.requestData?.isMemberManagedOrManagerManaged,
      isTaxpayerInUS: data?.requestData?.isTaxpayerInUS ?? false,
      mailingAddressOption: initialMailingOption,
      mailingAddress: {
        addressLine1: mailingAddress?.addressLine1 || '',
        addressLine2: mailingAddress?.addressLine2 || null,
        city: mailingAddress?.city || '',
        country: mailingAddress?.country || '',
        state: mailingAddress?.state || '',
        postalCode: mailingAddress?.postalCode || '',
      },
      operatingAddress: operatingAddress
        ? {
            addressLine1: operatingAddress?.addressLine1,
            addressLine2: operatingAddress?.addressLine2 || null,
            city: operatingAddress?.city,
            country: operatingAddress?.country,
            state: operatingAddress?.state,
            postalCode: operatingAddress?.postalCode,
          }
        : (defaultAddressSchema as unknown as IAddress),
      businessDescription: data?.requestData?.businessDescription,
      planToHireUSEmployeesInFirst12Months:
        data?.requestData?.planToHireUSEmployeesInFirst12Months ??
        YesNoNotSureOptions.NotSure,
      numberOfEmployees: data?.requestData?.numberOfEmployees ?? '',
      socialLink: data?.requestData?.socialLink,
      fiscalMonth: data?.requestData?.fiscalMonth
        ? data?.requestData?.fiscalMonth === '1'
          ? '12'
          : (parseInt(data?.requestData?.fiscalMonth, 10) - 1).toString()
        : suggestedMonthNumber,
      ...(!Object.keys(BusinessPurposeEnum).includes(
        data?.requestData?.companyService
      )
        ? { customBusinessPurpose: data.requestData?.companyService }
        : {}),
    },
  });

  const FEATURED_US_STATES = ['ADM1_US-DE', 'ADM1_US-WY', 'ADM1_US-TX'];
  const chosenJurisdiction = watch('jurisdiction');
  const isNonFeaturedState = Boolean(chosenJurisdiction && !FEATURED_US_STATES.includes(chosenJurisdiction));

  const createMailingFromOperating = (
    operatingAddr: IAddress | undefined
  ): IAddress | undefined => {
    if (
      !operatingAddr ||
      !operatingAddr.addressLine1 ||
      !operatingAddr.city ||
      !operatingAddr.state ||
      !operatingAddr.postalCode ||
      !operatingAddr.country
    ) {
      return undefined;
    }
    return {
      addressLine1: operatingAddr.addressLine1,
      addressLine2: operatingAddr.addressLine2 || null,
      city: operatingAddr.city,
      state: operatingAddr.state,
      postalCode: operatingAddr.postalCode,
      country: Country.US,
    } as IAddress;
  };

  useEffect(() => {
    const mailingOption = watch('mailingAddressOption');
    const operatingAddr = watch('operatingAddress') as IAddress | undefined;
    const operatingCountry = operatingAddr?.country;

    if (mailingOption === 'service') {
      unregister('mailingAddress');
      clearErrors('mailingAddress');
      clearErrors('mailingAddress.addressLine1');
      clearErrors('mailingAddress.city');
      clearErrors('mailingAddress.state');
      clearErrors('mailingAddress.postalCode');
      return;
    }

    if (mailingOption === 'sameAsPrincipal') {
      if (operatingCountry && operatingCountry !== Country.US) {
        // Auto-switch to manual if PPOB is not in US
        setValue('mailingAddressOption', 'manual');
        setValue('optForRegisterredAddressService', false);
        setValue('mailingAddress', {
          addressLine1: mailingAddress?.addressLine1,
          addressLine2: mailingAddress?.addressLine2 || null,
          city: mailingAddress?.city,
          state: mailingAddress?.state,
          postalCode: mailingAddress?.postalCode,
          country: Country.US,
        } as IAddress);
      } else {
        const mailingFromOperating = createMailingFromOperating(operatingAddr);
        if (mailingFromOperating) {
          setValue('mailingAddress', mailingFromOperating);
        }
      }
    }
  }, [
    watch('operatingAddress'),
    watch('mailingAddressOption'),
    mailingAddress,
    setValue,
  ]);

  const onSubmit = async (formData: CompanyInfoFormFields) => {
    const mailingChoice = formData.mailingAddressOption;

    if (
      mailingChoice !== 'service' &&
      formData?.mailingAddress &&
      formData?.mailingAddress.country !== Country.US
    ) {
      const countryName =
        CountryPackage.getCountryByCode(formData?.mailingAddress?.country)
          ?.name || formData?.mailingAddress?.country;

      setNotification({
        message: `Current mailing address is in ${countryName}. Only United States addresses are supported for mailing address.`,
        show: true,
        valid: false,
        title: 'Error',
      });
      return;
    }
    if (
      formData.corporationType === CorporationType.LLC &&
      !formData.isMemberManagedOrManagerManaged
    ) {
      setNotification({
        message:
          'Please select whether your LLC will be member-managed or manager-managed.',
        show: true,
        valid: false,
        title: 'Required',
      });
      return;
    }

    let fiscalMonth: string;
    // We collect the fiscal year end, but for legacy reasons the fiscalMonth field
    // on the backend represents the fiscal year START. This will be fixed by migrating fully
    // to the Commenda OS endpoint.
    if (formData.fiscalMonth === '12') {
      fiscalMonth = '1';
    } else {
      fiscalMonth = (parseInt(formData.fiscalMonth, 10) + 1).toString();
    }

    // Only include numberOfEmployees in API call when user selects 'Yes' and provides a valid number
    const shouldIncludeNumberOfEmployees =
      formData.planToHireUSEmployeesInFirst12Months ===
        YesNoNotSureOptions.Yes &&
      typeof formData.numberOfEmployees === 'number';

    const numberOfEmployeesToSend = shouldIncludeNumberOfEmployees
      ? formData.numberOfEmployees
      : undefined;

    const addressesToSend = [];

    if (
      formData?.operatingAddress &&
      formData.operatingAddress.addressLine1 &&
      formData.operatingAddress.city &&
      formData.operatingAddress.state &&
      formData.operatingAddress.postalCode &&
      formData.operatingAddress.country
    ) {
      const opAddress: IAddress = {
        addressLine1: formData.operatingAddress.addressLine1,
        addressLine2: formData.operatingAddress.addressLine2 ?? undefined,
        addressLine3: formData.operatingAddress.addressLine3 ?? undefined,
        city: formData.operatingAddress.city,
        state: formData.operatingAddress.state,
        postalCode: formData.operatingAddress.postalCode,
        country: formData.operatingAddress.country,
        ...(operatingAddress?.id ? { id: operatingAddress.id } : {}),
        type: AddressType.OperatingAddress,
      };
      addressesToSend.push(opAddress);
    }

    if (mailingChoice === 'sameAsPrincipal') {
      const existingOp = addressesToSend.find(
        (a: IAddress) => a.type === AddressType.OperatingAddress
      ) as IAddress | undefined;
      if (existingOp) {
        addressesToSend.push({
          ...existingOp,
          ...(mailingAddress?.id ? { id: mailingAddress.id } : {}),
          type: AddressType.MailingAddress,
          country: Country.US,
        } as IAddress);
      }
    } else if (
      mailingChoice === 'manual' &&
      formData?.mailingAddress &&
      formData.mailingAddress.addressLine1 &&
      formData.mailingAddress.city &&
      formData.mailingAddress.state &&
      formData.mailingAddress.postalCode &&
      formData.mailingAddress.country
    ) {
      const mailAddress: IAddress = {
        addressLine1: formData.mailingAddress.addressLine1,
        addressLine2: formData.mailingAddress.addressLine2 ?? undefined,
        addressLine3: formData.mailingAddress.addressLine3 ?? undefined,
        city: formData.mailingAddress.city,
        state: formData.mailingAddress.state,
        postalCode: formData.mailingAddress.postalCode,
        country: Country.US,
        ...(mailingAddress?.id ? { id: mailingAddress.id } : {}),
        type: AddressType.MailingAddress,
      };
      addressesToSend.push(mailAddress);
    }

    const res = await fetcher(
      `workRequest/${id}`,
      {
        method: 'POST',
        body: {
          requestData: {
            companyLegalName: formData.corporationName,
            companyLegalName2:
              formData.corporationType === CorporationType.LLC
                ? formData.companyLegalName2
                : undefined,
            companyNameSuffix:
              formData.corporationType === CorporationType.CCorp
                ? formData.companySuffix
                : 'LLC',
            incorporationJurisdiction: formData.jurisdiction,
            socialLink: formData.socialLink,
            fiscalMonth,
            businessDescription: formData.businessDescription,
            planToHireUSEmployeesInFirst12Months:
              formData.planToHireUSEmployeesInFirst12Months,
            ...(shouldIncludeNumberOfEmployees && {
              numberOfEmployees: numberOfEmployeesToSend,
            }),
            isTaxpayerInUS: formData?.isTaxpayerInUS,
            optForRegisterredAddressService:
              formData.optForRegisterredAddressService,
            corporationType: formData.corporationType,
            isMemberManagedOrManagerManaged:
              formData.corporationType === CorporationType.LLC
                ? formData.isMemberManagedOrManagerManaged
                : undefined,
          },
          ...(addressesToSend.length > 0
            ? {
                addresses: addressesToSend,
              }
            : {}),
          status: IncorporationRequestStatus.InProgress,
        },
      },
      token
    );

    if (!res.ok) {
      setNotification({
        message:
          'Something went wrong, please try again later or contact Team Commenda if you need help',
        show: true,
        valid: false,
        title: 'Error',
      });
      return;
    }

    await mutate();

    next();
  };

  return (
    <form
      className="flex flex-col gap-6 max-w-[500px]"
      onSubmit={handleSubmit(onSubmit, (formError: unknown) => {
        console.error(formError);
      })}
    >
      <IncorporationFormHeading
        text="Add Company info"
        additionalText="Please provide information about this company."
      />

      {!shouldHideCorporationType ? (
        <FieldWrapper _className="gap-2">
          <Label className="text-base font-bold text-neutral-1">
            Entity Type
          </Label>
          <span className="body-15-regular text-neutral-2">
            Please select your entity type
          </span>
          <div className="grid grid-cols-2 gap-3.5 mt-2">
            <RadioCardKS
              heading="C Corp"
              onClick={() => {
                clearErrors('corporationType');
                setValue('corporationType', CorporationType.CCorp);
                setValue('companySuffix', 'Inc.');
              }}
              isActive={watch('corporationType') === CorporationType.CCorp}
              description="Best for businesses planning to seek external investment and scale."
            />
            <RadioCardKS
              heading="LLC"
              onClick={() => {
                clearErrors('corporationType');
                setValue('corporationType', CorporationType.LLC);
              }}
              isActive={watch('corporationType') === CorporationType.LLC}
              description="Ideal for smaller businesses seeking flexibility and fewer formalities."
            />
          </div>
        </FieldWrapper>
      ) : null}

      <FieldWrapper>
        <Label className="text-base font-bold text-neutral-1">
          Choose Jurisdiction
        </Label>
        <span className="body-15-regular text-neutral-2">
          Which state do you want to register in ?
        </span>
        <div className="flex flex-col gap-3.5">
          <RadioCardKS
            heading="Delaware"
            description="A top choice for C Corps, offering strong legal protections for shareholders and established legal precedents."
            onClick={() => {
              clearErrors('jurisdiction');
              setValue('jurisdiction', 'ADM1_US-DE');
            }}
            isActive={watch('jurisdiction') === 'ADM1_US-DE'}
          />
          <RadioCardKS
            heading="Wyoming"
            description="Favorable for LLCs with its minimal regulations and privacy benefits. No state corporate income tax or annual franchise tax."
            onClick={() => {
              clearErrors('jurisdiction');
              setValue('jurisdiction', 'ADM1_US-WY');
            }}
            isActive={watch('jurisdiction') === 'ADM1_US-WY'}
          />

          <RadioCardKS
            heading="Texas"
            description="Robust networks of professionals, suppliers, and potential clients, along with no state income tax to help your business grow faster. Small corporations (Less than $1.23 M in annual revenue) are exempt from franchise tax."
            onClick={() => {
              clearErrors('jurisdiction');
              setValue('jurisdiction', 'ADM1_US-TX');
            }}
            isActive={watch('jurisdiction') === 'ADM1_US-TX'}
          />
          <RadioCardKS
            heading="Other"
            description="Our team will reach out to assist you with registering your company in a state not listed above."
            onClick={() => {
              clearErrors('jurisdiction');
              setValue('jurisdiction', '');
              setIsChoosingOtherState(true);
            }}
            isActive={isChoosingOtherState || isNonFeaturedState}
          />
        </div>
        {(isChoosingOtherState || isNonFeaturedState) && (
          <div className="mt-4">
            <Label
              htmlFor="otherState"
              className="text-base font-bold text-neutral-1"
            >
              Select State
            </Label>
            <span className="body-15-regular text-neutral-2 block mt-1 mb-2">
              Please select the state where you want to incorporate
            </span>
            <Select
              onValueChange={(val) => {
                setValue('jurisdiction', val);
                clearErrors('jurisdiction');
              }}
              value={isNonFeaturedState ? chosenJurisdiction : ''}
            >
              <SelectTrigger className="px-3 py-2.5 h-fit">
                <SelectValue placeholder="Select a state" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {optionsFor(Country.US).map((state) => (
                    <SelectItem key={state.value} value={state.value}>
                      {state.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {errors.jurisdiction && !watch('jurisdiction') && (
              <p className="text-sm text-red-500 mt-1">
                {errors.jurisdiction.message}
              </p>
            )}
          </div>
        )}
      </FieldWrapper>

      {watch('corporationType') === CorporationType.LLC ? (
        <FieldWrapper _className="gap-2">
          <Label className="text-base font-bold text-neutral-1">
            Will your LLC be member-managed or manager-managed?
          </Label>
          <div className="flex flex-col gap-3.5 mt-2">
            <RadioGroup
              className="flex flex-col gap-4"
              onValueChange={(val) => {
                setValue('isMemberManagedOrManagerManaged', val);
              }}
              value={watch('isMemberManagedOrManagerManaged')}
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="member-managed" id="member-managed" />
                  <Label htmlFor="member-managed">
                    Member-managed (Recommended)
                  </Label>
                </div>
                <p className="text-sm text-neutral-3 ml-6">
                  All owners help run the business and make decisions together.
                  (Best for most small businesses.)
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <RadioGroupItem
                    value="manager-managed"
                    id="manager-managed"
                  />
                  <Label htmlFor="manager-managed">Manager-managed</Label>
                </div>
                <p className="text-sm text-neutral-3 ml-6">
                  You appoint one or more managers to handle daily operations,
                  while other owners stay hands-off.
                </p>
              </div>
            </RadioGroup>
          </div>
        </FieldWrapper>
      ) : null}

      {/* New radio question about US employees in first 12 months */}
      <FieldWrapper _className="gap-2">
        <Label className="text-base font-bold text-neutral-1">
          Do you plan to hire employees in the United States in your first 12
          months as a business?
        </Label>
        <span className="body-15-regular text-neutral-2">
          If yes, you will need to file payroll tax to the IRS.
        </span>
        <div className="flex flex-col gap-3.5 mt-2">
          <RadioGroup
            className="flex flex-col gap-4"
            onValueChange={(val) =>
              setValue(
                'planToHireUSEmployeesInFirst12Months',
                val as YesNoNotSureOptions
              )
            }
            value={
              watch('planToHireUSEmployeesInFirst12Months') ||
              YesNoNotSureOptions.NotSure
            }
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem
                value={YesNoNotSureOptions.Yes}
                id="hire-us-yes"
              />
              <Label htmlFor="hire-us-yes">Yes</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value={YesNoNotSureOptions.No} id="hire-us-no" />
              <Label htmlFor="hire-us-no">No</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem
                value={YesNoNotSureOptions.NotSure}
                id="hire-us-not-sure"
              />
              <Label htmlFor="hire-us-not-sure">I'm not sure</Label>
            </div>
          </RadioGroup>
        </div>
        {watch('planToHireUSEmployeesInFirst12Months') ===
        YesNoNotSureOptions.Yes ? (
          <div className="mt-4">
            <Label
              className="text-base font-bold text-neutral-1"
              htmlFor="numberOfEmployees"
            >
              Approximately how many employees do you plan to hire in the first
              year?
            </Label>
            <div className="flex items-center gap-2 mt-1">
              <Tooltip content="This is only an estimate and does not have to be exact. We may provide this information to the US IRS for informational purposes.">
                <InfoToolTip />
              </Tooltip>
            </div>
            <Input
              placeholder="Number of employees"
              className="text-primary-1 py-2.5 px-3 h-[45px] mt-2"
              id="numberOfEmployees"
              {...register('numberOfEmployees', {
                setValueAs: (value) => {
                  if (value === '' || value === null || value === undefined)
                    return '';
                  const num = Number(value);
                  return isNaN(num) ? '' : num;
                },
              })}
              error={errors?.numberOfEmployees?.message}
            />
          </div>
        ) : null}
      </FieldWrapper>

      <FieldWrapper _className="gap-2">
        <Label
          htmlFor="corporationName"
          className="text-base font-bold text-neutral-1"
        >
          Corporation name
        </Label>
        <span className="body-15-regular text-neutral-2">
          We’ll check with the state to see if the name is available.
        </span>
        <div className="grid grid-cols-2 gap-3.5 mt-2">
          <Input
            placeholder="Your company name"
            className="text-primary-1 py-2.5 px-3 h-[45px]"
            id="corporationName"
            {...register('corporationName')}
            error={errors.corporationName?.message}
          />
          {watch('corporationType') === CorporationType.CCorp ? (
            <Select onValueChange={(val) => setValue('companySuffix', val)}>
              <SelectTrigger className="px-3 py-2.5 h-fit">
                <SelectValue placeholder={watch('companySuffix') || 'Inc.'} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {jurisdictionToSuffixMap[
                    watch('jurisdiction') ?? 'Unknown'
                  ]?.map((each) => (
                    <SelectItem key={each} value={each}>
                      {each}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          ) : (
            <Input
              value="LLC"
              className="text-primary-1 py-2.5 px-3"
              readOnly
              disabled
            />
          )}
        </div>
      </FieldWrapper>

      {watch('corporationType') === CorporationType.LLC && (
        <FieldWrapper _className="gap-2">
          <Label
            htmlFor="companyLegalName2"
            className="text-base font-bold text-neutral-1"
          >
            Fallback company name
          </Label>
          <span className="body-15-regular text-neutral-2">
            Please pick a fallback company name, in case your first choice is
            not available.
          </span>
          <div className="grid grid-cols-2 gap-3.5 mt-2">
            <Input
              placeholder="Your second company name option"
              className="text-primary-1 py-2.5 px-3 h-[45px]"
              id="companyLegalName2"
              {...register('companyLegalName2')}
              error={errors.companyLegalName2?.message}
            />
            <Input
              value="LLC"
              className="text-primary-1 py-2.5 px-3"
              readOnly
              disabled
            />
          </div>
        </FieldWrapper>
      )}

      <FieldWrapper _className="gap-2">
        <Label
          htmlFor="companyWebsite"
          className="text-base font-bold text-neutral-1"
        >
          Company website
        </Label>
        <div className="mt-4">
          <Input
            placeholder="www.website.com"
            className="text-primary-1 py-2.5 px-3 h-[45px]"
            id="companyWebsite"
            {...register('socialLink')}
            error={errors.socialLink?.message}
          />
        </div>
      </FieldWrapper>

      <FieldWrapper _className="gap-2">
        <Label
          htmlFor="businessDescription"
          className="text-base font-bold text-neutral-1"
        >
          What does your business do?
        </Label>
        <span className="body-15-regular text-neutral-2">
          Provide a brief description of your business, including the primary
          business activities.
        </span>
        <div className="mt-2">
          <Textarea
            placeholder="Write a description"
            className="py-2.5 px-3 w-full"
            id="businessDescription"
            {...register('businessDescription')}
            error={errors.businessDescription?.message}
          />
        </div>
      </FieldWrapper>

      <FieldWrapper _className="gap-2">
        <Label
          htmlFor="fiscalYear"
          className="text-base font-bold text-neutral-1 flex items-center gap-2"
        >
          <span>Fiscal year end date</span>
          <Tooltip content="Your fiscal year can be any consecutive 12-month period, though the most common starts on January 1 and ends December 31.">
            <InfoToolTip />
          </Tooltip>
        </Label>
        <div className="mt-4">
          <FiscalMonthSelectMenuKS
            {...register('fiscalMonth')}
            error={errors?.fiscalMonth?.message}
            defaultValue={watch('fiscalMonth') || suggestedMonthNumber}
            suggestedMonthNumber={parseInt(suggestedMonthNumber, 10)}
          />
        </div>
      </FieldWrapper>

      <FieldWrapper _className="gap-4">
        <Label className="text-base font-bold text-neutral-1">
          Principal place of business
        </Label>
        <span className="body-15-regular text-neutral-2">
          The location where the corporation's officers and directors sit and
          where business decisions are made.
        </span>
        <div>
          <AddressInput
            id="operating-address"
            address={watch('operatingAddress') as IAddress}
            onChange={(a) => {
              setValue('operatingAddress', a);
            }}
            error={{
              addressLine1: !watch('operatingAddress.addressLine1')
                ? errors?.operatingAddress?.addressLine1?.message
                : null,
              city: !watch('operatingAddress.city')
                ? errors?.operatingAddress?.city?.message
                : null,
              postalCode: !watch('operatingAddress.postalCode')
                ? errors?.operatingAddress?.postalCode?.message
                : null,
              state: !watch('operatingAddress.state')
                ? errors?.operatingAddress?.state?.message
                : undefined,
              country: !watch('operatingAddress.country')
                ? errors?.operatingAddress?.country?.message
                : undefined,
            }}
          />
        </div>
      </FieldWrapper>

      <FieldWrapper _className="gap-4">
        <Label className="text-base font-bold text-neutral-1">
          US mailing address (where you can receive mail in the US)
        </Label>
        <div className="flex flex-col gap-3">
          <RadioGroup
            className="flex flex-col gap-3"
            value={watch('mailingAddressOption') || 'manual'}
            onValueChange={(val) => {
              setValue(
                'mailingAddressOption',
                val as 'service' | 'manual' | 'sameAsPrincipal'
              );
              if (val === 'service') {
                setValue('optForRegisterredAddressService', true);
                setValue('mailingAddress', {
                  addressLine1: '',
                  addressLine2: null,
                  city: '',
                  country: '',
                  state: '',
                  postalCode: '',
                });
                clearErrors('mailingAddress');
                clearErrors('mailingAddress.addressLine1');
                clearErrors('mailingAddress.city');
                clearErrors('mailingAddress.state');
                clearErrors('mailingAddress.postalCode');
              } else if (val === 'sameAsPrincipal') {
                setValue('optForRegisterredAddressService', false);
                const operatingAddressValue = watch('operatingAddress') as
                  | IAddress
                  | undefined;
                if (
                  operatingAddressValue &&
                  operatingAddressValue.addressLine1 &&
                  operatingAddressValue.city &&
                  operatingAddressValue.state &&
                  operatingAddressValue.postalCode &&
                  operatingAddressValue.country
                ) {
                  setValue('mailingAddress', {
                    addressLine1: operatingAddressValue.addressLine1,
                    addressLine2: operatingAddressValue.addressLine2 || null,
                    city: operatingAddressValue.city,
                    state: operatingAddressValue.state,
                    postalCode: operatingAddressValue.postalCode,
                    country: Country.US,
                  } as IAddress);
                }
              } else {
                setValue('optForRegisterredAddressService', false);
                setValue('mailingAddress', {
                  addressLine1: mailingAddress?.addressLine1,
                  addressLine2: mailingAddress?.addressLine2 || null,
                  city: mailingAddress?.city,
                  state: mailingAddress?.state,
                  postalCode: mailingAddress?.postalCode,
                  country: Country.US,
                } as IAddress);
              }
            }}
          >
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="service" id="mailing-service" />
                <Label htmlFor="mailing-service">
                  Use Commenda's registered address service
                </Label>
              </div>
              <p className="text-sm text-neutral-3 ml-6">
                We'll handle mail forwarding and compliance for you.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="manual" id="mailing-manual" />
                <Label htmlFor="mailing-manual">
                  Use my own mailing address
                </Label>
              </div>
              <p className="text-sm text-neutral-3 ml-6">
                Enter your US mailing address.
              </p>
            </div>
            {watch('operatingAddress')?.country === Country.US && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <RadioGroupItem
                    value="sameAsPrincipal"
                    id="mailing-same-principal"
                  />
                  <Label htmlFor="mailing-same-principal">
                    Same as principal place of business
                  </Label>
                </div>
                <p className="text-sm text-neutral-3 ml-6">
                  Use your business's main address as the mailing address.
                </p>
              </div>
            )}
          </RadioGroup>
        </div>
        {watch('mailingAddressOption') === 'manual' ? (
          <div>
            <AddressInput
              id="mailing-address"
              disabled={{ country: true }}
              address={{
                ...(watch('mailingAddress') as IAddress),
                country: Country.US,
              }}
              onChange={(a) => {
                setValue('mailingAddress', a);
                if (a.addressLine1 && errors?.mailingAddress?.addressLine1) {
                  clearErrors('mailingAddress.addressLine1');
                }
                if (a.city && errors?.mailingAddress?.city) {
                  clearErrors('mailingAddress.city');
                }
                if (a.state && errors?.mailingAddress?.state) {
                  clearErrors('mailingAddress.state');
                }
                if (a.postalCode && errors?.mailingAddress?.postalCode) {
                  clearErrors('mailingAddress.postalCode');
                }
              }}
              error={{
                addressLine1: errors?.mailingAddress?.addressLine1?.message,
                city: errors?.mailingAddress?.city?.message,
                postalCode: errors?.mailingAddress?.postalCode?.message,
                state: errors?.mailingAddress?.state?.message,
              }}
            />
          </div>
        ) : null}
      </FieldWrapper>
      <div className="flex gap-4">
        <Button type="submit" disabled={isSubmitting}>
          <span>Proceed</span>
          <ArrowNarrowRight />
        </Button>
      </div>
    </form>
  );
};

export default CompanyInfo;
